import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, ArrowRight, Globe, Building2, Tag, TrendingDown, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { formatPrice, CATEGORY_LABELS } from '../lib/utils';
import { applySeo, websiteJsonLd } from '../lib/seo';

export default function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [tlds, setTlds] = useState([]);
  const [stats, setStats] = useState(null);
  const [featuredDeals, setFeaturedDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInitialData();
    applySeo({
      title: 'Domain-Preise vergleichen | Domain-Preisvergleich.de',
      description: 'Vergleichen Sie Domain-Preise aller großen Anbieter: Registrierung, Verlängerung und Transfer für 1–5 Jahre. Ohne Sternchen, ohne Kleingedrucktes.',
      path: '/',
      jsonLd: websiteJsonLd(),
    });
  }, []);

  const loadInitialData = async () => {
    try {
      const [tldData, statsData] = await Promise.all([
        api.getTlds(null, true),
        api.getStats()
      ]);
      setTlds(tldData);
      setStats(statsData);

      // Load featured deals for popular TLDs
      const popularTlds = ['.de', '.com', '.net', '.eu'];
      const deals = [];
      for (const tld of popularTlds) {
        try {
          const compare = await api.compare(tld, 1, 'registration', 'total');
          if (compare.results.length > 0) {
            deals.push({
              tld: tld,
              cheapest: compare.results[0],
              total_providers: compare.total_providers,
              has_promo: compare.results.some(r => r.has_promotion)
            });
          }
        } catch (e) { /* ignore */ }
      }
      setFeaturedDeals(deals);
    } catch (e) {
      console.error('Fehler beim Laden:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (q) => {
    setSearchQuery(q);
    if (q.length > 0) {
      try {
        const results = await api.search(q);
        setSearchResults(results);
        setShowResults(true);
      } catch (e) {
        setSearchResults([]);
      }
    } else {
      setShowResults(false);
    }
  }, []);

  const handleSelectTld = (tld) => {
    setShowResults(false);
    navigate(`/tld/${encodeURIComponent(tld)}`);
  };

  const handleSubmitSearch = (e) => {
    e.preventDefault();
    const tld = searchQuery.startsWith('.') ? searchQuery : '.' + searchQuery;
    navigate(`/tld/${encodeURIComponent(tld)}`);
  };

  const categoryGroups = tlds.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 via-white to-sky-50/30 py-16 md:py-24" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Der faire Domain-Preisvergleich
              </h1>
              <p className="text-base md:text-lg text-muted-foreground mb-2 max-w-xl">
                Registrierung, Verlängerung und Transfer bei deutschen & internationalen Anbietern. Preise für 1–5 Jahre im Blick.
              </p>
              <p className="text-sm text-muted-foreground mb-8 max-w-xl">
                Ohne Sternchen, ohne Kleingedrucktes: Wir zeigen den durchschnittlichen Jahrespreis über 5 Jahre, damit Lockangebote im ersten Jahr nicht täuschen.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSubmitSearch} className="relative" data-testid="domain-search-form">
                <div className="flex gap-2 p-2 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="TLD eingeben, z.B. .de, .com, .net ..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      onFocus={() => searchQuery && setShowResults(true)}
                      className="pl-10 border-0 shadow-none focus-visible:ring-0 text-base"
                      data-testid="domain-search-input"
                    />
                  </div>
                  <Button type="submit" size="lg" className="rounded-xl px-6" data-testid="domain-search-submit-button">
                    Vergleichen
                  </Button>
                </div>

                {/* Search Results Dropdown */}
                {showResults && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden" data-testid="domain-search-suggestions-list">
                    {searchResults.slice(0, 8).map(tld => (
                      <button
                        key={tld.tld}
                        onClick={() => handleSelectTld(tld.tld)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-sky-50 transition-colors text-left border-b border-slate-50 last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-slate-900">{tld.tld}</span>
                          <span className="text-xs text-muted-foreground">{tld.description}</span>
                        </div>
                        <Badge variant="outline" className="text-[10px]">{CATEGORY_LABELS[tld.category]}</Badge>
                      </button>
                    ))}
                  </div>
                )}
              </form>

              {/* Quick TLD chips */}
              <div className="flex flex-wrap gap-2 mt-4">
                {['.de', '.com', '.net', '.org', '.eu', '.io'].map(tld => (
                  <button
                    key={tld}
                    onClick={() => navigate(`/tld/${encodeURIComponent(tld)}`)}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-sky-300 hover:text-sky-700 transition-colors"
                    data-testid={`quick-tld-${tld.replace('.', '')}`}
                  >
                    {tld}
                  </button>
                ))}
              </div>
            </div>

            {/* Trust Panel */}
            <div className="lg:col-span-2">
              <Card className="rounded-2xl shadow-sm">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Auf einen Blick</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 rounded-xl bg-sky-50">
                      <div className="text-2xl font-bold text-sky-700 tabular-nums">{stats?.active_providers || 0}</div>
                      <div className="text-xs text-slate-500 mt-1">Anbieter</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-emerald-50">
                      <div className="text-2xl font-bold text-emerald-700 tabular-nums">{stats?.tlds || 0}</div>
                      <div className="text-xs text-slate-500 mt-1">TLDs</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-amber-50">
                      <div className="text-2xl font-bold text-amber-700 tabular-nums">{stats?.active_promotions || 0}</div>
                      <div className="text-xs text-slate-500 mt-1">Aktionen</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-slate-50">
                      <div className="text-2xl font-bold text-slate-700 tabular-nums">{stats?.prices || 0}</div>
                      <div className="text-xs text-slate-500 mt-1">Preise</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Deals */}
      {featuredDeals.length > 0 && (
        <section className="py-12 md:py-16" data-testid="featured-deals-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>Beliebte TLDs</h2>
                <p className="text-sm text-muted-foreground mt-1">Günstigste Preise bei ausgewählten Top-Level-Domains</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredDeals.map(deal => (
                <Link
                  key={deal.tld}
                  to={`/tld/${encodeURIComponent(deal.tld)}`}
                  className="group"
                >
                  <Card className="rounded-2xl hover:shadow-md transition-shadow duration-200 h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>{deal.tld}</span>
                        {deal.has_promo && (
                          <Badge className="bg-amber-50 text-amber-900 border-amber-200 text-[10px]">Aktion</Badge>
                        )}
                      </div>
                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-sm text-muted-foreground">ab</span>
                        <span className="text-xl font-bold text-emerald-700 tabular-nums">
                          {formatPrice(deal.cheapest.calculation.total)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        bei {deal.cheapest.provider_name} · {deal.total_providers} Anbieter
                      </p>
                      <div className="flex items-center text-xs text-sky-600 font-medium group-hover:text-sky-700">
                        Preise vergleichen <ArrowRight className="w-3 h-3 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TLD Categories */}
      <section className="py-12 md:py-16 bg-white" data-testid="tld-categories-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-8" style={{fontFamily: 'Space Grotesk'}}>Alle TLDs nach Kategorie</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(categoryGroups).map(([cat, catTlds]) => (
              <Card key={cat} className="rounded-2xl">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      cat === 'country' ? 'bg-blue-50 text-blue-600' :
                      cat === 'new' ? 'bg-purple-50 text-purple-600' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      <Globe className="w-4 h-4" />
                    </div>
                    <h3 className="font-semibold text-slate-900">{CATEGORY_LABELS[cat] || cat}</h3>
                    <Badge variant="secondary" className="text-[10px] ml-auto">{catTlds.length}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {catTlds.map(t => (
                      <Link
                        key={t.tld}
                        to={`/tld/${encodeURIComponent(t.tld)}`}
                        className="px-2.5 py-1 bg-slate-50 hover:bg-sky-50 hover:text-sky-700 rounded-lg text-sm text-slate-700 transition-colors"
                      >
                        {t.tld}
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SEO / Info Section */}
      <section className="py-12 md:py-16 bg-slate-50/60" data-testid="info-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Warum Domain-Preisvergleich?
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                Der Domainmarkt ist voll von Sternchen, Kleingedrucktem und Lockangeboten. Um herauszufinden, wo
                eine Domain langfristig am günstigsten ist, musste man bisher Stunden investieren. Wir sind
                angetreten, diesen Zustand zu ändern – mit einem übersichtlichen Vergleich für alle
                Domainendungen, ohne versteckte Kosten.
              </p>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Wie wir vergleichen
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                Viele Anbieter locken im ersten Jahr mit günstigen Preisen. Wir zeigen deshalb den
                durchschnittlichen Jahrespreis über 5 Jahre, damit Sie direkt den bestmöglichen Vergleich sehen.
                Unsere Preisdaten werden regelmäßig automatisch aktualisiert und validiert.
              </p>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Gutes zu wissen
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                Achten Sie beim Domain-Kauf auf Transfer-Gebühren, das Kleingedruckte und den Auth-Code. In unserem{' '}
                <Link to="/ratgeber" className="text-sky-600 hover:text-sky-700">Domain-Kauf-Ratgeber</Link>{' '}
                fassen wir die wichtigsten Stolperfallen zusammen – und in{' '}
                <Link to="/domain-technik" className="text-sky-600 hover:text-sky-700">Domain-Technik</Link>{' '}
                erklären wir, wie Domains eigentlich funktionieren.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
